import { PageOptions } from '@graphcommerce/framer-next-pages'
import { mergeDeep, useLazyQuery, useQuery } from '@graphcommerce/graphql'
import {
  AddProductsToCartButton,
  AddProductsToCartError,
  AddProductsToCartForm,
  AddProductsToCartFormProps,
  AddProductsToCartQuantity,
  getProductStaticPaths,
  jsonLdProduct,
  jsonLdProductOffer,
  ProductCustomizable,
  productPageCategory,
  ProductPagePrice,
  ProductPagePriceTiers,
  ProductShortDescription,
} from '@graphcommerce/magento-product'
import { BundleProductOptions } from '@graphcommerce/magento-product-bundle'
import {
  ConfigurableContextProvider,
  ConfigurableName,
  ConfigurablePrice,
  ConfigurablePriceTiers,
  defaultConfigurableOptionsSelection,
} from '@graphcommerce/magento-product-configurable'
import { DownloadableProductOptions } from '@graphcommerce/magento-product-downloadable'
import { jsonLdProductReview } from '@graphcommerce/magento-review'
import { redirectOrNotFound, Money, StoreConfigDocument } from '@graphcommerce/magento-store'
import { ProductWishlistChipDetail } from '@graphcommerce/magento-wishlist'
import {
  GetStaticProps,
  JsonLd,
  LayoutHeader,
  LayoutTitle,
  isTypename,
  useFabSize,
} from '@graphcommerce/next-ui'
import { Trans } from '@lingui/react'
import { Box, Divider, Link, Typography } from '@mui/material'
import { GetStaticPaths } from 'next'
import { useRouter } from 'next/router'
import { useContext, useEffect, useState } from 'react'
import {
  RowProduct,
  RowRenderer,
  Usps,
} from '../../components'
import { ConfigurableProductOptions } from '../../components/ChipLabCustom/Configurable/ConfigurableProductOptions'
import ContextReceiver from '../../components/ChipLabCustom/ProductContext/ContextReceiver'
import ProductContext from '../../components/ChipLabCustom/ProductContext/product-context'
import { LayoutDocument } from '../../components/Layout/Layout.gql'
import { CategoryListDocument } from '../../graphql/CategoryList.gql'
import { ProductPage2Document, ProductPage2Query } from '../../graphql/ProductPage2.gql'
import { TemplateProductPageDocument, TemplateProductPageQuery } from '../../graphql/TemplateProductPage.gql'
import { graphqlSharedClient, graphqlSsrClient } from '../../lib/graphql/graphqlSsrClient'
import ConfigurableLayout from '../../components/ChipLabCustom/Configurable/ConfigurableLayout'
import { ConfigurableProductPageGallery } from '../../components/ChipLabCustom/Configurable/ConfigurableProductPageGallery'
import { LayoutFull, LayoutFullProps } from 'components/Layout/LayoutFull'
import { AttributeTetheringDocument } from 'components/GraphCMS/AttributeTether/AttributeTether.gql'
import { GetCustomReviewsDocument } from 'graphql/GetCustomReviews.gql'
import { ProductReviewChip } from 'components/ChipLabCustom/Reviews/ProductReviewChip'
import { GetMaxAllowedDocument } from 'graphql/GetMaxAllowed.gql'
import { SKUOrganizationDocument } from 'components/GraphCMS/SKUOrganizer/SKUOrganization.gql'
import { AdjustedProductSidebarDelivery } from 'components/ChipLabCustom/Configurable/AdjustedProductSidebarDelivery'
import useProductIDs from 'components/ChipLabCustom/ID/useProductIDs'
import { ProductPageMeta } from 'components/ChipLabCustom/Product/ProductPageMeta'
import Sidebar from 'components/ChipLabCustom/Configurable/Sidebar'
import CustomizeButtonDirect from 'components/ChipLabCustom/CustomizeButton/CustomizeButtonDirect'
import ConfigurableAddAndCustomize from 'components/ChipLabCustom/Configurable/ConfigurableAddAndCustomize'

type Props = ProductPage2Query & Pick<AddProductsToCartFormProps, 'defaultValues'> & TemplateProductPageQuery & { SkuData: any, categoryList: any, AttributeTether?: any, qtyEngaged?: boolean }

type RouteProps = { url: string }
type GetPageStaticPaths = GetStaticPaths<RouteProps>
type GetPageStaticProps = GetStaticProps<LayoutFullProps, Props, RouteProps>

function ProductPage(props: Props) {
  const { products, relatedUpsells, usps, sidebarUsps, pages, defaultValues, typeProducts, SkuData, AttributeTether, qtyEngaged } = props

  const product = mergeDeep(products, relatedUpsells)?.items?.[0]
  const router = useRouter();
  const typeProduct = typeProducts?.items?.[0]
  const { filters } = useContext(ProductContext);
  const [update, setUpdate] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(product);
  const [hasOne, setHasOne] = useState(true);
  const [imageRefresh, setImageRefresh] = useState(false);
  const [isCustomizable, setIsCustomizable] = useState(false);
  const [addAndCustomize, setAddAndCustomize] = useState(false);
  const [imageName, setImageName] = useState('');
  const [mediaGallery, setMediaGallery] = useState<any[]>([]);
  const [rating, setRating] = useState<number>(product?.rating_summary ?? 0);
  const [shopByUse, setShopByUse] = useState<boolean>(false);
  const [mainReviewCount, setMainReviewCount] = useState<number>(product?.review_count ?? 0);
  const [isUse, setIsUse] = useState(false);
  const [currentSku, setCurrentSku] = useState(product?.sku ?? "");
  const [currentTypeProduct, setCurrentTypeProduct] = useState(typeProduct);
  const [by_use_id, setByUseId] = useState<any>(null);
  const [selected, setSelected] = useState<boolean>(false);
  const tetherList = AttributeTether?.data?.pages?.[0]?.content?.filter(item => item.__typename === "AttributeTether" && item.attributeName === "val");
  const sidebar = AttributeTether?.data?.pages?.[0]?.content?.find(item => item.__typename === "AttributeTether" && item.connectedUid === "sidebar" && item.uid === product?.url_key);
  const { allIDs } = useProductIDs();
  const { data: typeData, loading: typeLoading } = useQuery(TemplateProductPageDocument, { variables: { sku: currentSku } });

  useEffect(() => {
    if (!typeLoading) {
      if (typeData) {
        const typeDataProduct = typeData?.typeProducts?.items?.[0];
        setCurrentTypeProduct(typeDataProduct);
      }
    }
  }, [typeData, typeLoading]);

  useEffect(() => {
    setCurrentProduct(product);
  }, [currentSku])

  const [getReviews] = useLazyQuery(GetCustomReviewsDocument);

  useEffect(() => {
    if (imageName !== "") {
      if (currentProduct?.[imageName]) {
        if (currentProduct[imageName]?.url !== "") {
          setMediaGallery([currentProduct[imageName]]);
          return;
        }
      }
    }
    if (currentProduct?.media_gallery) {
      setMediaGallery([...currentProduct?.media_gallery]);
      return;
    }
  }, [imageName, currentProduct])

  let sku: String = "";

  async function retrieveReviews(filters: string) {
    const r = await getReviews({
      variables: {
        productIds: filters,
        pageSize: 1,
        currentPage: 1
      }
    });
    setShopByUse(true);
    setRating(r.data?.GetReviews?.total_rating ?? product?.rating_summary ?? 0);
  }

  useEffect(() => {
    setImageRefresh((prev) => !prev);
  }, [hasOne, update]);

  useEffect(() => {
    console.log(SkuData)
    if (SkuData?.data?.pages[0]?.content) {
      for (let i of SkuData.data.pages[0].content) {
        if (i.__typename === "SkuOrganizer") {
          if (router.query.url !== undefined) {
            sku = router.query.url?.toString();
            if (sku === i.sku) {
              if (i.use === "add-and-customize") {
                setAddAndCustomize(true);
              } else {
                setIsCustomizable(true);
              }
              const tetherList = AttributeTether?.data?.pages?.[0]?.content;
              for (let tether of tetherList) {
                if (tether?.__typename === "AttributeTether") {
                  if (tether?.connectedName === 'use-only') {
                    if (tether?.attributeName === sku) {
                      setImageName(tether?.uid);
                      retrieveReviews(allIDs);
                      setIsUse(true);
                      setByUseId(tether?.connectedUid);
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }, [])

  useEffect(() => {
    var _learnq = _learnq || [];
    var item = {
      "ProductName": product?.name ?? "N/A",
      "ProductID": product?.id ?? 0,
      "SKU": product?.sku ?? "N/A",
      "Categories": product?.categories ?? [],
      "ImageURL": product?.media_gallery?.[0] ?? "N/A",
      "URL": router.pathname,
      "Brand": "N/A",
      "Price": product?.price_range?.minimum_price?.final_price?.value?.toString() ?? "",
      "CompareAtPrice": 14.99
    };
    _learnq.push(["track", "Viewed Product", item]);
  }, [])

  if (!product?.sku || !product.url_key) return null

  if (!isCustomizable) return (
    <>
      <LayoutHeader floatingMd>
        <LayoutTitle size='small' component='span'>
          {product.name}
        </LayoutTitle>
      </LayoutHeader>
      <JsonLd
        item={{
          '@context': 'https://schema.org',
          ...jsonLdProduct(product),
          ...jsonLdProductOffer(product),
          ...jsonLdProductReview(product),
        }}
      />
      <ProductPageMeta {...product} />

      <AddProductsToCartForm key={product.uid} defaultValues={defaultValues}>
        <ConfigurableProductPageGallery
          url_key={product.url_key}
          media_gallery={product.media_gallery}
          sx={(theme) => ({
            '& .SidebarGallery-sidebar': { display: 'grid', rowGap: theme.spacings.sm },
          })}
        >
          {addAndCustomize ? <>
            <ConfigurableAddAndCustomize
              product={product}
              currentTypeProduct={currentTypeProduct}
              currentSku={currentSku}
              sidebar={sidebar}
              setDisableButton={setDisableButton}
              qtyEngaged={qtyEngaged}
              update={update}
              addAndCustomize={addAndCustomize}
              disableButton={disableButton}
              SkuData={SkuData}
              tetherList={tetherList}
              by_use_id={by_use_id}
              sidebarUsps={sidebarUsps}
            />

          </> : <>
            <div>
              {isTypename(product, ['ConfigurableProduct', 'BundleProduct']) && (
                <Typography component='div' variant='body2' color='text.disabled'>
                  <Trans
                    id='As low as <0/>'
                    components={{ 0: <Money {...product.price_range.minimum_price.final_price} /> }}
                  />
                </Typography>
              )}

              <Typography variant='h2' component='h1' gutterBottom>
                {isTypename(product, ['ConfigurableProduct']) ? (
                  <ConfigurableName product={product} />
                ) : (
                  product.name
                )}
              </Typography>

              <ProductShortDescription
                short_description={product?.description}
                sx={(theme) => ({ mb: theme.spacings.xs })}
              />

              <ProductReviewChip review_count={product?.review_count} rating={product.rating_summary} reviewSectionId='reviews' />

              {sidebar && <Sidebar sidebar={sidebar} />}
            </div>

            {isTypename(product, ['ConfigurableProduct']) && (

              <ConfigurableProductOptions
                product={product}
                currentSku={currentSku}
                setDisableButton={setDisableButton}
                optionEndLabels={{
                  size: (
                    <Link
                      href='/modal/product/global/size'
                      rel='nofollow'
                      color='primary'
                      underline='hover'
                    >
                      <Trans id='Which size is right?' />
                    </Link>
                  ),
                }}
              />
            )}
            {isTypename(product, ['BundleProduct']) && (
              <BundleProductOptions product={product} layout='stack' />
            )}
            {isTypename(product, ['DownloadableProduct']) && (
              <DownloadableProductOptions product={product} />
            )}
            {!isTypename(product, ['GroupedProduct']) && <ProductCustomizable product={product} />}

            <Divider />

            {qtyEngaged && <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                columnGap: theme.spacings.md,
              })}
            >
              <AddProductsToCartQuantity sx={{ flexShrink: '0' }} />

              <AddProductsToCartError>
                <Typography component='div' variant='h3' lineHeight='1'>
                  {isTypename(product, ['ConfigurableProduct']) ? (
                    <ConfigurablePrice product={product} />
                  ) : (
                    <ProductPagePrice product={product} />
                  )}
                </Typography>
              </AddProductsToCartError>
            </Box>}

            {isTypename(product, ['ConfigurableProduct']) ? (
              <ConfigurablePriceTiers product={product} />
            ) : (
              <ProductPagePriceTiers product={product} />
            )}

            <AdjustedProductSidebarDelivery product={product} />

            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'start',
                columnGap: theme.spacings.xs,
              })}
            >
              <AddProductsToCartButton fullWidth product={product} />
              <ProductWishlistChipDetail {...product} />
            </Box>
            <Usps usps={sidebarUsps} size='small' />
          </>}
        </ConfigurableProductPageGallery>
      </AddProductsToCartForm>

      {pages?.[0] && (
        <RowRenderer
          content={pages?.[0].content}
          renderer={{
            RowProduct: (rowProps) => (
              <RowProduct
                {...rowProps}
                {...product}
                items={products?.items}
                aggregations={products?.aggregations}
              />
            ),
          }}
        />
      )}
    </>
  )
  return (<>
    <LayoutHeader floatingMd>
      <LayoutTitle size='small' component='span'>
        {product.name}
      </LayoutTitle>
    </LayoutHeader>
    <JsonLd
      item={{
        '@context': 'https://schema.org',
        ...jsonLdProduct(product),
        ...jsonLdProductOffer(product),
        ...jsonLdProductReview(product),
      }}
    />
    <ProductPageMeta {...product} />

    <AddProductsToCartForm key={product.uid} defaultValues={defaultValues}>
      <ConfigurableProductPageGallery
        url_key={currentProduct?.url_key}
        media_gallery={mediaGallery}
        imageRefresh={imageRefresh}
        sx={(theme) => ({
          '& .SidebarGallery-sidebar': { display: 'grid', rowGap: theme.spacings.sm },
        })}
      >
        <div>

          <Typography variant='h2' component='h1' gutterBottom>
            {isTypename(product, ['ConfigurableProduct']) ? (
              <ConfigurableName product={product} />
            ) : (
              product.name
            )}
          </Typography>

          <ProductShortDescription
            short_description={product?.description}
            sx={(theme) => ({ mb: theme.spacings.xs })}
          />

          <ProductReviewChip review_count={mainReviewCount} rating={rating} reviewSectionId='reviews' />

          {sidebar && <Sidebar sidebar={sidebar} />}
        </div>

        {isTypename(product, ['ConfigurableProduct']) && (
          <ConfigurableContextProvider {...currentTypeProduct} sku={currentSku}>
            <ProductPageMeta {...product} />

            <ContextReceiver products={products} setUpdate={setUpdate} filters={filters} />

            {update === true &&
              <ConfigurableLayout
                product={product}
                disableButton={disableButton}
                router={router}
                setDisableButton={setDisableButton}
                currentProduct={currentProduct}
                setCurrentProduct={setCurrentProduct}
                hasOne={hasOne}
                setHasOne={setHasOne}
                usps={sidebarUsps}
                isCustomizable={isCustomizable}
                isUse={isUse}
                currentSku={currentSku}
                setCurrentSku={setCurrentSku}
                selected={selected}
                setSelected={setSelected}
                tetherList={tetherList}
                defaultValues={defaultValues}
                by_use_id={by_use_id} />}

            {update === false &&
              <ConfigurableLayout
                product={product}
                disableButton={disableButton}
                router={router}
                setDisableButton={setDisableButton}
                currentProduct={currentProduct}
                setCurrentProduct={setCurrentProduct}
                hasOne={hasOne}
                setHasOne={setHasOne}
                usps={sidebarUsps}
                isCustomizable={isCustomizable}
                isUse={isUse}
                currentSku={currentSku}
                setCurrentSku={setCurrentSku}
                selected={selected}
                setSelected={setSelected}
                tetherList={tetherList}
                defaultValues={defaultValues}
                by_use_id={by_use_id} />}

          </ConfigurableContextProvider>
        )}
        <Divider />

        <Usps usps={sidebarUsps} size='small' />
      </ConfigurableProductPageGallery>
    </AddProductsToCartForm>

    {pages?.[0] && (
      <RowRenderer
        content={pages?.[0].content}
        renderer={{
          RowProduct: (rowProps) => (
            <RowProduct
              {...rowProps}
              {...product}
              setMainReviewCount={setMainReviewCount}
              shopByUse={shopByUse ? allIDs : undefined}
              typeProduct={typeProduct}
              items={products?.items}
              aggregations={products?.aggregations}
            />
          ),
        }}
      />
    )}
  </>);
}

ProductPage.pageOptions = {
  Layout: LayoutFull,
} as PageOptions

export default ProductPage

export const getStaticPaths: GetPageStaticPaths = async ({ locales = [] }) => {
  if (import.meta.graphCommerce.legacyProductRoute) return { paths: [], fallback: false }
  if (process.env.NODE_ENV === 'development') return { paths: [], fallback: 'blocking' }

  const path = (locale: string) => getProductStaticPaths(graphqlSsrClient(locale), locale)
  const paths = (await Promise.all(locales.map(path))).flat(1)

  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetPageStaticProps & any = async ({ params, locale }) => {
  if (import.meta.graphCommerce.legacyProductRoute) return { notFound: true }

  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)

  const urlKey = params?.url ?? '??'

  const conf = client.query({ query: StoreConfigDocument })
  const productPage = staticClient.query({
    query: ProductPage2Document,
    variables: { url: urlKey, urlKey },
  })
  const productPage2 = staticClient.query({
    query: ProductPage2Document,
    variables: { url: 'product/global', urlKey },
  })
  const layout = staticClient.query({ query: LayoutDocument })

  const product = (await productPage).data.products?.items?.find((p) => p?.url_key === urlKey)
  if (!product) return redirectOrNotFound(staticClient, conf, params, locale)

  const categoryList = staticClient.query({
    query: CategoryListDocument,
    variables: { ID: 33 },
  })

  const category = productPageCategory(product)
  const up =
    category?.url_path && category?.name
      ? { href: `/${category.url_path}`, title: category.name }
      : { href: `/`, title: 'Home' }

  const typeProductPage = staticClient.query({
    query: TemplateProductPageDocument,
    variables: { urlKey },
  })

  const SkuData = client.query({ query: SKUOrganizationDocument, variables: { url: "templates" } })

  const AttributeTether = client.query({ query: AttributeTetheringDocument, variables: { url: "product/global" } })

  const max_qty = staticClient.query({ query: GetMaxAllowedDocument, variables: { productID: product.id?.toString() ?? "" } });
  const maxData = (await max_qty).data.GetMaximumQtyAllowedInCart?.qty;

  return {
    props: {
      ...defaultConfigurableOptionsSelection(urlKey, client, (await productPage)?.data?.pages.length > 0 ? (await productPage)?.data : (await productPage2)?.data),
      ...(await layout).data,
      ...(await typeProductPage).data,
      categoryList: (await categoryList).data,
      SkuData: await SkuData,
      AttributeTether: await AttributeTether,
      qtyEngaged: maxData && maxData > 1,
      apolloState: await conf.then(() => client.cache.extract()),
      up,
    },
    revalidate: 60 * 20,
  }
}
